import { EnvironmentConfig } from "./environment-config.model";

/** Phase 1 attachment groups. A type covers every category page under it. */
export const SHOP_PHASE_1 = ["skid-steer", "mini-skid-steer", "excavator", "mini-excavator", "forklift", "telehandler"] as const;
/** Phase 2 mixes equipment departments (wood chippers, stump grinders, trenchers) with attachment groups. */
export const SHOP_PHASE_2 = ["wood-chippers", "stump-grinders", "trenchers", "wheel-loader", "compact-wheel-loader", "dozer"] as const;
export const SHOP_PHASE_3 = ["motor-grader", "backhoe-loader", "smooth-drum-roller", "tractor"] as const;
export const SHOP_PHASED_TYPES: readonly string[] = [...SHOP_PHASE_1, ...SHOP_PHASE_2, ...SHOP_PHASE_3];

const ATTACHMENT_TYPES = new Set<string>([
  "skid-steer", "mini-skid-steer", "excavator", "mini-excavator", "forklift", "telehandler",
  "wheel-loader", "compact-wheel-loader", "dozer", "motor-grader", "backhoe-loader", "smooth-drum-roller", "tractor",
]);

const ALIASES: Record<string, string> = {
  "wood-chipper": "wood-chippers",
  "stump-grinder": "stump-grinders",
  trencher: "trenchers",
};

export function shopTypeHandle(value: string): string {
  const handle = (value || "").trim().toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return ALIASES[handle] || handle;
}

/** Explicit config wins. An empty or unsubstituted placeholder uses Phase 1 in production and every phased type otherwise. */
export function parseShopLiveMachineTypes(raw: string | undefined, production: boolean): ReadonlySet<string> {
  if (!raw || raw.trim() === "" || raw.includes("%ENV_")) {
    return new Set(production ? SHOP_PHASE_1 : SHOP_PHASED_TYPES);
  }
  const handles = raw.split(",").map(shopTypeHandle).filter(handle => SHOP_PHASED_TYPES.includes(handle) || handle.length > 0 && !handle.startsWith("%"));
  return new Set(handles.filter(handle => handle.length > 0));
}

export function shopLiveMachineTypes(config?: Partial<EnvironmentConfig>): ReadonlySet<string> {
  const runtime = typeof window !== "undefined" ? (window as { shopLiveMachineTypes?: string }).shopLiveMachineTypes : "";
  const raw = runtime && runtime.trim() ? runtime : config?.shopLiveMachineTypes;
  return parseShopLiveMachineTypes(raw, config?.production === "true");
}

export function shopTypeIsLive(handle: string, config?: Partial<EnvironmentConfig>): boolean {
  return shopLiveMachineTypes(config).has(shopTypeHandle(handle));
}

export function shopProductIsLive(product: { departmentHandle?: string; machineTypes?: string[] }, config?: Partial<EnvironmentConfig>): boolean {
  const live = shopLiveMachineTypes(config);
  if ((product?.machineTypes || []).some(type => live.has(shopTypeHandle(type)))) return true;
  const department = shopTypeHandle(product?.departmentHandle || "");
  return department !== "" && department !== "attachments" && live.has(department);
}

export function liveAttachmentMachineTypes<T extends { handle: string }>(types: readonly T[], config?: Partial<EnvironmentConfig>): T[] {
  const live = shopLiveMachineTypes(config);
  return types.filter(type => live.has(type.handle));
}

export function shopDepartmentIsLive(department: string, config?: Partial<EnvironmentConfig>): boolean {
  const handle = shopTypeHandle(department);
  const live = shopLiveMachineTypes(config);
  if (!handle || handle === "attachments") return [...ATTACHMENT_TYPES].some(type => live.has(type));
  return live.has(handle);
}

export function shopCollectionIsLive(department: string, machineType: string, config?: Partial<EnvironmentConfig>): boolean {
  const machine = shopTypeHandle(machineType);
  if (machine) return shopTypeIsLive(machine, config);
  return shopDepartmentIsLive(department || "attachments", config);
}

/** Old product URLs do not name a machine. Redirect them only when every phased type is live. */
export function shopAllPhasedTypesAreLive(config?: Partial<EnvironmentConfig>): boolean {
  const live = shopLiveMachineTypes(config);
  return SHOP_PHASED_TYPES.every(type => live.has(type));
}

const ATTACHMENT_TYPE_HANDLES = new Set<string>([
  "skid-steer", "mini-skid-steer", "excavator", "mini-excavator", "forklift", "telehandler",
  "wheel-loader", "compact-wheel-loader", "dozer", "motor-grader", "backhoe-loader", "smooth-drum-roller", "tractor",
]);

export function legacyShopRedirectIsLive(department: string, machineType: string, commerceRedirect: string, config?: Partial<EnvironmentConfig>): boolean {
  if (typeof commerceRedirect === "string" && commerceRedirect.includes("/products/")) return shopAllPhasedTypesAreLive(config);
  if (!department && !machineType && typeof commerceRedirect === "string") {
    const parts = commerceRedirect.split("?")[0].split("/").filter(Boolean);
    if (parts[0] === "shop") parts.shift();
    if (parts[0] === "attachments") return shopCollectionIsLive("attachments", ATTACHMENT_TYPE_HANDLES.has(parts[1] || "") ? parts[1] : "", config);
    if (parts[0] && parts[0] !== "products") return shopCollectionIsLive(parts[0], "", config);
  }
  return shopCollectionIsLive(department || "", machineType || "", config);
}

declare const environment: EnvironmentConfig;

export function currentShopLiveMachineTypes(): ReadonlySet<string> {
  try {
    return shopLiveMachineTypes(typeof environment === "undefined" ? undefined : environment);
  } catch {
    return shopLiveMachineTypes(undefined);
  }
}
